<template>
  <div class="login">
    <div class="login__image">
      <div class="container">
        <img
          class="image"
          src="@/assets/icons/login-img.jpg"
          alt="Noutoapteekk"
        />
      </div>
    </div>
    <div class="login__content">
      <div class="container">
        <div class="login__content-head">
          <div class="login__content-head-lang">
            <LangChange />
          </div>
        </div>
        <div class="login__content-form">
          <Loading v-if="loading" />
          <InfoBanner variant="block" />
          <div class="form">
            <div class="form-title">
              <p>{{ $t('login') }}</p>
            </div>
            <div class="form-inputs">
              <Input
                v-model="email"
                type="email"
                name="email"
                :label="$t('email')"
                :value="email"
                for-name="email"
                validation="required|email"
                :validation-messages="{
                  required: $t('validation.required'),
                  email: $t('validation.email')
                }"
                @change="handleChange"
              />
              <Input
                v-model="password"
                type="password"
                name="password"
                :label="$t('password')"
                :value="password"
                for-name="password"
                validation="required"
                :validation-messages="{
                  required: $t('validation.required')
                }"
                @change="handleChange"
              />
            </div>
            <div class="form-action">
              <div
                v-if="forgotPassword === false"
                class="forgot-password"
                @click="openForgotPassword"
              >
                <p>{{ $t('forgotPassword') }}</p>
              </div>
              <div v-show="status.error" class="form-error">
                <div class="error" role="alert">
                  {{ $t(status.message || 'error') }}
                </div>
              </div>
            </div>
            <div class="form-buttons">
              <Button
                id="login"
                class="button-log"
                variant="logIn"
                type="button"
                :title="$t('login')"
                data-testid="loginButton"
                @click="signIn"
              />
              <div class="login-microsoft">
                <Button
                  id="authenticateUsingProvider"
                  class="button-log"
                  variant="blackButton"
                  type="button"
                  :title="$t('loginWithMicrosoft')"
                  data-testid="loginloginWithMicrosoftButton"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapState, mapActions } from 'vuex';

import LangChange from '@/views/Language.vue';
import Loading from '@/views/Loading.vue';

import router from '@/router';
import { InputFileEvent } from '@/types';

import { goToPickupBox } from '@/utils/goToPickupBox';
import { goToEditPharmacy } from '@/utils/goToEditPharmacy';

@Options({
  components: {
    Loading,
    LangChange
  },
  data() {
    return {
      email: null,
      password: null,
      loading: false,
      forgotPassword: false,
      pharmacyLoading: true,
      boxId: ''
    };
  },
  computed: {
    ...mapState('account', ['status', 'user']),
    ...mapState('pharmacy', ['pharmacy'])
  },
  created() {
    this.$i18n.locale = 'fi';
  },
  methods: {
    ...mapActions('account', ['login', 'logout', 'loginWithMicrosoft']),
    ...mapActions('pharmacy', ['getPharmacy']),
    handleChange(e: InputFileEvent) {
      this[e.target.name] = e.target.value;
    },
    async signIn() {
      this.forgotPassword = false;

      if (this.email && this.password) {
        this.loading = true;

        await this.login({
          email: this.email,
          password: this.password
        });

        this.loading = false;

        if (this.pharmacyAccesses) {
          if (this.user.pharmacyAccesses?.length <= 1) {
            this.getTabs();
          }
        } else {
          this.getTabs();
        }
      }

      // Keep the language picked on the login screen when the account has no
      // saved language yet (e.g. the user still has to choose a pharmacy).
      const savedLocale = localStorage.getItem('locale');

      if (savedLocale) {
        this.$i18n.locale = savedLocale;
      }
    },
    async getTabs() {
      this.loading = true;
      this.pharmacyLoading = true;

      if (this.user) {
        if (
          this.user?.role.name !== 'janitor' &&
          this.user?.role.name !== 'chain_admin'
        ) {
          await this.getPharmacy(this.user?.pharmacyId);
        }
      }

      this.boxId = this.pharmacy?.pickupBoxes[0];
      this.pharmacyLoading = false;

      if (!this.pharmacyLoading && this.user) {
        if (
          this.user?.role.name === 'janitor' ||
          this.user?.role.name === 'chain_admin'
        ) {
          return this.goToProfile();
        }
        if (this.pharmacy?.remomediServices.pickupbox && this.boxId) {
          return goToPickupBox(
            this.user?.pharmacyId,
            this.pharmacy?.pickupBoxes[0]
          );
        } else {
          return this.pharmacy?.remomediServices.mobileApp
            ? router.push({ name: 'CallerAuthentication' })
            : goToEditPharmacy(this.user.pharmacyId);
        }
      }

      this.loading = false;
    },
    goToProfile() {
      router.push({
        name: 'Profile'
      });
    },
    openForgotPassword() {
      this.forgotPassword = true;
      router.push({ name: 'ForgotPassword' });
    },
    loginAD() {
      this.loginWithMicrosoft();
    }
  }
})
export default class LoginComponent extends Vue {}
</script>

<style lang="scss" scoped>
.login {
  display: flex;
  .login__image {
    width: 50%;
    height: 100vh;
    .container {
      width: 100%;
      height: 100%;
      background-color: $light-grey;
      .image {
        max-width: 100vh;
        height: 100vh;
      }
    }
  }
  .login__content {
    width: 50%;
    height: 100vh;
    .container {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: $light-grey;
      .login__content-head {
        .login__content-head-lang {
          position: absolute;
          width: 172px;
          height: 42px;
          right: 32px;
          top: 32px;
          border-radius: 8px;
          background-color: $light-grey;
        }
      }
      .login__content-form {
        max-width: 416px;
        min-height: 345px;
        border-radius: 8px;
        .form {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 32px;
          border-radius: 8px;
          background: $white;
          .form-title {
            width: 352px;
            margin-bottom: 30px;
            p {
              font-family: 'Ubuntu-Medium';
              font-size: $twentyFourSize;
              line-height: $twentyEightSize;
              font-weight: 400;
            }
          }
          .form-inputs {
            width: 352px;
          }
          .form-action {
            display: flex;
            justify-content: space-between;
            width: 352px;
            margin-bottom: 24px;
            margin-top: 16px;
            .forgot-password {
              cursor: pointer;
              p {
                font-size: $twelveSize;
                line-height: $fourteenSize;
                font-weight: 400;
                color: $main-green;
              }
            }
            .form-error {
              .error {
                background-color: $white;
                border: none;
                border-radius: 0;
                padding: 0;
                margin-bottom: 0;
                color: $messageError;
                font-size: $twelveSize;
                text-align: center;
              }
            }
          }
          .form-buttons {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            .button-log {
              width: 170px;
            }
          }
          .login-microsoft {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 170px;
            border: 1px solid $label;
            border-radius: 8px;
            .fa {
              margin-left: 5px;
              cursor: pointer;
            }
            .button-log {
              width: 100%;
            }
          }
        }

        @media (min-width: 900px) {
          width: 100%;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.form-inputs {
  .input {
    &:last-child {
      margin-bottom: 0;
    }
    .formkit-outer {
      width: 352px;
      .formkit-wrapper {
        .formkit-inner {
          .formkit-input {
            &:hover {
              border: 1px solid $main-green;
            }
          }
        }
      }
    }
  }
}
</style>
